/**
 * THE BLOCK LIBRARY, HELD IN THE BROWSER (2026-08-19).
 *
 * `/sfmc-blocks?op=list&deep=1` is measured at 16–60s: it authenticates against
 * SFMC, lists the category tree, queries the folder, and then fetches EVERY asset
 * individually. Nothing in the Studio can assemble a template until it answers —
 * so the Studio sat on the flat template preview for the best part of a minute and
 * then swapped the canvas under the user.
 *
 * The monolith never had this problem because it ships the payload on disk
 * (`ech/ech-blocks.js`, loaded by a <script> tag before the Studio boots). A React
 * app served from a pipeline has no such file, so the equivalent is the browser's
 * own storage: keep the last good payload, adopt it SYNCHRONOUSLY on the first
 * render, and refresh from the network behind it.
 *
 * The shape follows the repo's existing stale-while-revalidate precedent
 * (`dupJobs.ts` → `dupLS`/`dupLSPut`, monolith `dupLS`, "modal felt slow"), with the
 * three rules that path learned the hard way:
 *
 *  1. A STALE SNAPSHOT MUST NEVER WIN OVER A REAL ANSWER. Adoption is guarded by
 *     `adoptedSig`: the snapshot is only ever adopted when NOTHING has been adopted
 *     yet, and the live payload is adopted unconditionally when its bytes differ.
 *  2. A FAILED REFRESH KEEPS THE SNAPSHOT. Nothing here clears state on error —
 *     `echSnapSave` is only ever called from a resolved fetch, and the consumers
 *     fall back to "do I have blocks?" rather than to null.
 *  3. A REFRESH THAT BRINGS DIFFERENT BYTES STILL BUMPS THE VERSION. This one is
 *     load-bearing and easy to break: `StudioView`'s `echRebuildKeepEdits` path
 *     (audit row 20) fires only when `echVersion() + echTplVersion()` CHANGES, so a
 *     cache that skipped re-registration would silently kill the carry-edits-onto-
 *     fresher-markup behaviour. `echAdopt()` therefore re-registers — and so bumps
 *     both versions — whenever the fingerprint moves, and only then.
 *
 * WHY A FINGERPRINT AND NOT A TIMESTAMP. "Different bytes" has to mean different
 * MARKUP, not a different fetch. Re-registering on every pull would rebuild the
 * document on every Studio open for no reason; comparing `builtAt` would do the
 * same. `echSnapSig` hashes id + name + html for every block, so an unchanged
 * library is a no-op and one edited block in Content Builder is a rebuild.
 */
import { registerEchBlocks, echTypes } from "./echRegistry";
import { echLoadLists } from "./echLists";
import { echLoadTemplates, templateShellName } from "./echTemplates";
import { findShell, findShellNamed, type SfmcBlock } from "./sfmcBlocks";
import { markBareShell, setShellProvider } from "../email/document";

/** bump when the STORED shape changes — an old entry is then ignored, not parsed */
export const SNAP_KEY = "esBlockSnap.v1";
/**
 * 24h. Long enough that a normal day of requests never waits on SFMC, short enough
 * that a block edited in Content Builder can't haunt a cold open for a week. An
 * EXPIRED snapshot is treated as absent — the Studio shows its honest loading state
 * rather than assembling from markup nobody has checked since yesterday. The
 * refresh behind it is what makes that safe.
 */
/* 15 MINUTES, NOT 24 HOURS (2026-08-19). Layne, on Teams: "I made an update to the amenities
   block, and I updated the Coming Soon template in SFMC. I'm seeing it reflect occasionally in
   the dashboard, but not consistently." That IS this cache. A day-long snapshot means a block
   she edits in Content Builder can be invisible here until tomorrow, and whether it shows
   depends on which cache happened to be warm — exactly the "occasionally" she describes.
   The snapshot exists to kill the 40s cold open, and it still does: it paints first, then the
   network answer replaces it in the same session. Short TTL costs one extra fetch; a long one
   costs trust in what the dashboard is showing. There is also an explicit refresh now, so
   nobody has to wait this out. */
export const SNAP_TTL_MS = 15 * 60 * 1000;

interface SnapEnvelope {
  /** when the payload was written */
  t: number;
  sig: string;
  blocks: SfmcBlock[];
}

/**
 * A 32-bit FNV-1a over id + name + html of every block. Not a security hash — a
 * change detector, and cheap enough (~200KB of string) to run on every pull.
 */
export function echSnapSig(blocks: SfmcBlock[]): string {
  let h = 0x811c9dc5;
  const push = (s: string) => {
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
  };
  for (const b of blocks || []) {
    push(String(b?.id ?? ""));
    push("");
    push(String(b?.name ?? ""));
    push("");
    push(String(b?.html ?? ""));
    push("\x01");
    /* THE GUARDRAILS ARE PART OF THE FINGERPRINT (2026-08-25).
       They were not, and `html` cannot stand in for them: the API returns the markup with the
       `@GUARDRAILS` header STRIPPED (measured on ECH_CopyBlock — 2,151 bytes, no `@GUARDRAILS`,
       no patternMessage text). So a guardrail-only edit in Content Builder — a new `pattern`, a
       changed `max`, a new `patternMessage` — left id, name and html byte-identical, the
       fingerprint unmoved, and `echAdopt` skipped re-registration. The browser kept serving the
       OLD rules out of the snapshot.

       That is exactly Layne 2026-08-25: she authored a pattern on ECH_CopyBlock.Headline, the
       live block carries it, the regex genuinely fails "ENJOY", the deployed bundle has the
       checking code — and no warning ever appeared. Character caps DID work, because those were
       already in the guardrails when the snapshot was taken. Same shape as her 2026-08-19
       "occasionally" report; that was the 24h TTL, this is the sibling the TTL fix missed.

       A hard refresh could NOT clear it — the snapshot is in the browser's own storage, so the
       only escapes were the 15-minute TTL or clearing site data, which is why it read as "the
       feature is broken" rather than "the cache is stale".
       A null guardrail hashes as "" and matches the previous behaviour exactly. */
    push(b?.guardrails ? JSON.stringify(b.guardrails) : "");
    push("");
  }
  return (blocks || []).length + "-" + h.toString(36);
}

/** Persist the last good payload. Silent on a quota/private-mode failure — a
 *  snapshot that cannot be written must never break the pull that produced it. */
export function echSnapSave(blocks: SfmcBlock[]): void {
  try {
    if (!blocks || !blocks.length) return; /* never cache an empty answer */
    const env: SnapEnvelope = { t: Date.now(), sig: echSnapSig(blocks), blocks };
    localStorage.setItem(SNAP_KEY, JSON.stringify(env));
  } catch { /* quota / private mode — the app is unaffected */ }
}

/** The stored payload, or null when there is none, it is unreadable, or it has
 *  aged past the TTL. */
export function echSnapLoad(): SfmcBlock[] | null {
  try {
    const raw = localStorage.getItem(SNAP_KEY);
    if (!raw) return null;
    const env = JSON.parse(raw) as SnapEnvelope | null;
    if (!env || !Array.isArray(env.blocks) || !env.blocks.length) return null;
    if (!env.t || Date.now() - env.t > SNAP_TTL_MS) return null;
    return env.blocks;
  } catch { return null; }
}

/** how old the stored payload is, in ms, or -1 when there isn't one (diagnostics) */
export function echSnapAge(): number {
  try {
    const raw = localStorage.getItem(SNAP_KEY);
    if (!raw) return -1;
    const env = JSON.parse(raw) as SnapEnvelope | null;
    return env && env.t ? Date.now() - env.t : -1;
  } catch { return -1; }
}

/* ── ADOPTION ─────────────────────────────────────────────────────────────────
   "Adopting" a payload = registering its blocks (`echRegistry`) and loading the
   templates its `ECH_TPL_*` blocks declare (`echTemplates`), in that order — a
   placement resolves to a REAL block only if the block registered first. Both
   consumers (`StudioView`, `useEchTemplateList`) route through here so the
   fingerprint check lives in exactly one place. */

let adoptedSig: string | null = null;
let adoptedBlocks: SfmcBlock[] | null = null;
/** "sfmc" once a live pull has been adopted — a snapshot can never take it back */
let adoptedFrom: "" | "snapshot" | "sfmc" = "";
let adoptedShell: string | null = null;

/**
 * Register + load a payload if it differs from what is already adopted.
 * Returns true when it was adopted (i.e. both versions were bumped).
 *
 * The `echTypes().length` guard makes this self-healing after a `__resetEch()`:
 * the registry is module-scope and a test seam can empty it underneath us, and
 * skipping registration then would leave the app with no blocks at all.
 */
export function echAdopt(blocks: SfmcBlock[], from: "snapshot" | "sfmc"): boolean {
  if (!blocks || !blocks.length) return false;
  const sig = echSnapSig(blocks);
  if (adoptedSig === sig && echTypes().length) {
    /* same bytes — do NOT bump the versions; a rebuild from identical markup is
       pure churn (and would reset block ids under an open Inspector). Record that
       a live pull confirmed it, so a later snapshot adoption stays blocked. */
    if (from === "sfmc") { adoptedFrom = "sfmc"; adoptedBlocks = blocks; }
    return false;
  }
  registerEchBlocks(blocks);
  echLoadTemplates(blocks);
  /* controlled vocabularies ride the same payload — see echLists.ts */
  echLoadLists(blocks);
  adoptedSig = sig;
  adoptedBlocks = blocks;
  adoptedFrom = from;
  adoptedShell = findShell(blocks); /* once per payload — the balance gate is not free per render */
  return true;
}

/**
 * THE SHELL IS PART OF THE ADOPTED PAYLOAD (2026-09-15). `ECH_Blank_Template` rides
 * the same `/sfmc-blocks` pull as Layne's blocks, so it is resolved here, once per
 * adoption, and handed to `renderDocument` as its default — every render of the
 * document (form preview, submit, Studio export, template fill) wraps in the same
 * SFMC shell without each caller having to know it exists.
 */
export function echShell(): string | null { return adoptedShell; }

/**
 * THE SHELL IS PER TEMPLATE WHEN THE TEMPLATE SAYS SO (2026-10-06, Layne's Option B).
 * A template whose `@GUARDRAILS` carries `"shell":"ECH_V2_Shell"` renders in that block;
 * every other template — and every template while the named block is missing from the
 * pull (prod drops `_TEST` assets) — keeps the default shell above. A named shell is
 * registered as BARE so `renderDocument` adds nothing to it (email/document.ts).
 *
 * App sets the request's template once per render (`echSetActiveTemplate`), so the
 * callers that render through the provider — the form rail, the submit — follow it
 * without each one being handed a name.
 */
let activeTpl = "";
export function echSetActiveTemplate(name: string | null | undefined): void { activeTpl = String(name || ""); }
export function echActiveTemplate(): string { return activeTpl; }

const namedCache = new Map<string, string | null>();
/** the shell `name` renders in, resolved from `blocks` (default: the adopted payload) */
export function echShellFrom(blocks: SfmcBlock[] | null | undefined, name?: string | null): string | null {
  const src = blocks && blocks.length ? blocks : adoptedBlocks;
  const tpl = name === undefined || name === null ? activeTpl : String(name);
  if (src && tpl) {
    const sn = templateShellName(src, tpl);
    if (sn) {
      const raw = src.find((b) => String(b.name || "").toLowerCase() === sn.toLowerCase());
      const key = raw ? sn.toLowerCase() + "\u0000" + String(raw.html || "") : "";
      let html = key && namedCache.has(key) ? namedCache.get(key)! : null;
      if (key && !namedCache.has(key)) { html = findShellNamed(src, sn); namedCache.set(key, html); }
      if (html) { markBareShell(html); return html; }
    }
  }
  if (src === adoptedBlocks) return adoptedShell;
  /* a pull with no default shell keeps the one in hand — echSnap rule 2 */
  return (src && src.length ? findShell(src) : null) || adoptedShell;
}
export function echShellFor(name?: string | null): string | null { return echShellFrom(null, name); }
setShellProvider(() => echShellFor());

/**
 * Adopt the stored payload — SYNCHRONOUSLY, from a render, before first paint.
 * A no-op when anything has been adopted already (rule 1) or there is no usable
 * snapshot. Returns true when the Studio can assemble immediately.
 */
export function echAdoptSnapshot(): boolean {
  if (adoptedSig) return false;
  const blocks = echSnapLoad();
  if (!blocks) return false;
  return echAdopt(blocks, "snapshot");
}

/** the payload currently adopted — `findShell()` needs the raw blocks, and on the
 *  snapshot path there is no fetch to read them from yet */
export function echAdoptedBlocks(): SfmcBlock[] | null { return adoptedBlocks; }
/** whether the blocks in play came from the network or from the browser */
export function echAdoptedFrom(): "" | "snapshot" | "sfmc" { return adoptedFrom; }
/** true when the block library is usable right now (snapshot or live) */
export function echLibReady(): boolean { return !!adoptedSig; }

/** test seam / cold-load measurement: forget the adoption AND the stored payload */
export function __resetEchSnap(): void {
  adoptedSig = null;
  adoptedBlocks = null;
  adoptedFrom = "";
  adoptedShell = null;
  activeTpl = "";
  namedCache.clear();
  try { localStorage.removeItem(SNAP_KEY); } catch { /* private mode */ }
}
